import { initI18n, type I18n } from './i18n.js';
import { el, mount, clear } from './dom.js';
import { api } from './session.js';
import { LandingView } from './views/landing.js';
import { OnboardingView } from './views/onboarding.js';
import { IdentityStep } from './views/identityStep.js';
import { ScenarioScreen, OutcomeScreen } from './views/scenario.js';
import { DebriefScreen } from './views/debrief.js';
import { PrivacyView } from './views/privacy.js';
import { AboutResearchView } from './views/aboutResearch.js';
import { SurveyView } from './views/survey.js';
import { ReplayConsentView } from './views/replayConsent.js';
import { clearState, saveState } from './state.js';
import { setPageSeo } from './seo.js';
import { isExpertMode } from './expertMode.js';

// Remove the SEO-fallback block as soon as JS boots — it exists only for
// no-JS LLM crawlers. Doing it here (instead of inline in index.html) keeps
// the CSP strict: script-src 'self' without 'unsafe-inline'.
document.getElementById('seo-fallback')?.remove();

const root = document.getElementById('app') as HTMLElement;
const footer = document.getElementById('footer') as HTMLElement;

function errorBanner(i18n: I18n, msg: string): HTMLElement {
  return el('div', { class: 'error-banner', role: 'alert' }, msg || i18n.t('errors.generic'));
}

function renderFooter(
  i18n: I18n,
  ctx: { onExit: () => void; onPrivacy: () => void; onDelete: () => void },
) {
  clear(footer);
  const sep = () => el('span', { class: 'footer-sep', 'aria-hidden': 'true' }, '·');
  footer.appendChild(
    el('div', { class: 'footer-inner' }, [
      el('a', { href: '#', onClick: (e: Event) => { e.preventDefault(); ctx.onExit(); } },
        i18n.t('footer.exit')),
      sep(),
      el('a', { href: '#', onClick: (e: Event) => { e.preventDefault(); ctx.onPrivacy(); } },
        i18n.t('footer.privacy')),
      sep(),
      el('a', { href: '#', onClick: (e: Event) => { e.preventDefault(); ctx.onDelete(); } },
        i18n.t('footer.delete')),
      sep(),
      el('a', {
        href: 'https://www.linkedin.com/company/confluxlab',
        target: '_blank',
        rel: 'noopener noreferrer',
      }, i18n.t('footer.linkedin')),
    ]),
  );
}

function setShell(wide: boolean) {
  root.classList.toggle('shell-wide', wide);
}

async function run() {
  const i18n = await initI18n();
  window.addEventListener('allot:langChanged', () => {
    void showLanding();
  });

  async function showLanding() {
    clearState();
    setShell(false);
    setPageSeo('landing');
    mount(root, LandingView({
      i18n,
      onStart: async () => {
        try {
          // Kick off session creation and extras-bundle load in parallel:
          // the user will need scenarios/debrief keys starting with the
          // first scenario render, so we may as well prefetch them while
          // the server creates the session.
          const [session] = await Promise.all([
            api.startSession(i18n.lang, true),
            i18n.ensureExtras(),
          ]);
          saveState({ index: 0, lang: session.lang });
          showOnboarding();
        } catch (err) {
          mount(root, errorBanner(i18n, (err as Error).message));
        }
      },
      onOpenPrivacy: () => {
        setPageSeo('privacy');
        mount(root, PrivacyView({ i18n, onBack: () => void showLanding() }));
      },
      onOpenAboutResearch: () => { void showAboutResearch(); },
    }));
    renderFooter(i18n, {
      onExit: () => void showLanding(),
      onPrivacy: () => {
        setPageSeo('privacy');
        mount(root, PrivacyView({ i18n, onBack: () => void showLanding() }));
      },
      onDelete: async () => { await api.deleteSession().catch(() => undefined); clearState(); void showLanding(); },
    });
  }

  async function showAboutResearch() {
    setShell(false);
    setPageSeo('aboutResearch');
    // Title override for SEO — researcher-facing page has its own title.
    document.title = i18n.t('research.page_title');
    mount(root, AboutResearchView({
      i18n,
      onBack: () => void showLanding(),
    }));
    renderFooter(i18n, {
      onExit: () => void showLanding(),
      onPrivacy: () => {
        setPageSeo('privacy');
        mount(root, PrivacyView({ i18n, onBack: () => void showLanding() }));
      },
      onDelete: async () => { await api.deleteSession().catch(() => undefined); clearState(); void showLanding(); },
    });
    // Reflect in URL (browsers / LLM agents bookmarkable).
    if (typeof history !== 'undefined' && location.pathname !== '/about-research') {
      try { history.replaceState(null, '', '/about-research'); } catch { /* ignore */ }
    }
  }

  function showOnboarding() {
    setShell(false);
    setPageSeo('onboarding');
    mount(root, OnboardingView({
      i18n,
      // §2a v2.3 — the optional identity step sits between role confirmation
      // and the first scenario. Both submit and skip land on scenario 0;
      // skipping stores nothing (prefer_not_to_say is an explicit choice).
      onConfirmed: () => { showIdentityStep(); },
      onError: (err) => {
        mount(root, errorBanner(i18n, (err as Error).message));
      },
    }));
  }

  // §2a v2.3 — optional player identity profile (religion / ethnicity /
  // citizenship). Feeds ONLY the server-side identity_distance metric and
  // research aggregation (G17 isolation); never the narrative.
  function showIdentityStep() {
    setShell(false);
    setPageSeo('onboarding');
    mount(root, IdentityStep({
      i18n,
      onDone: () => { void showScenario(0); },
      onSkip: () => { void showScenario(0); },
    }));
  }

  async function showScenario(index: number) {
    try {
      const view = await api.scenario(index, isExpertMode());
      saveState({ index, lang: i18n.lang });
      setShell(false);
      setPageSeo('scenario');
      mount(root, ScenarioScreen({
        i18n,
        view,
        onSubmitted: (ack) => {
          setPageSeo('outcome');
          mount(root, OutcomeScreen({
            i18n,
            ack,
            onContinue: () => {
              if (ack.atEnd) void showDebrief();
              else void showScenario(ack.nextIndex);
            },
          }));
        },
        onError: (err) => {
          mount(root, errorBanner(i18n, (err as Error).message));
        },
      }));
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 401 || status === 409) {
        await showLanding();
        return;
      }
      mount(root, errorBanner(i18n, (err as Error).message));
    }
  }

  async function showDebrief() {
    try {
      const view = await api.debrief();
      setShell(true);
      setPageSeo('debrief');
      mount(root, DebriefScreen({
        i18n,
        view,
        onReplay: () => void showReplayConsent(),
        onWithdraw: async () => {
          await api.deleteSession().catch(() => undefined);
          clearState();
          void showLanding();
        },
        onClose: () => void showSurvey(() => void showLanding()),
      }));
    } catch (err) {
      mount(root, errorBanner(i18n, (err as Error).message));
    }
  }

  function showSurvey(onDone: () => void) {
    setShell(false);
    setPageSeo('survey');
    mount(root, SurveyView({ i18n, onDone }));
  }

  // §8 replay protection: explicit consent step between the debrief and the
  // creation of a new session.
  // Decline → landing, no new session created (the cookie expires on its own).
  // Continue → POST /api/session with replayConsent=linkPriorSession.
  function showReplayConsent() {
    setShell(false);
    setPageSeo('replayConsent');
    mount(root, ReplayConsentView({
      i18n,
      onDecline: () => void showLanding(),
      onContinue: async (linkPriorSession) => {
        try {
          const session = await api.startSession(i18n.lang, true, linkPriorSession);
          clearState();
          saveState({ index: 0, lang: session.lang });
          showOnboarding();
        } catch (err) {
          mount(root, errorBanner(i18n, (err as Error).message));
        }
      },
    }));
  }

  // SEO.3 — researcher-facing URL route: render AboutResearchView when
  // someone lands via /about-research (from search/LLM citation or a
  // bookmarked deep link). No session flow side effects.
  if (typeof location !== 'undefined' && location.pathname.startsWith('/about-research')) {
    await showAboutResearch();
    return;
  }

  // Restore session on reload if the cookie is still valid. We only call
  // the API when local state suggests a session may exist — otherwise
  // new visitors pay 1–2s of blocking session lookup before seeing the
  // landing. This slashes LCP for the common "first visit" case.
  const hasLocalState = (() => {
    try { return !!localStorage.getItem('allot.state'); } catch { return false; }
  })();
  const snapshot = hasLocalState ? await api.currentSession().catch(() => null) : null;
  if (snapshot && snapshot.total > 0) {
    if (snapshot.lang !== i18n.lang) {
      await i18n.switch(snapshot.lang as Parameters<typeof i18n.switch>[0]);
    }
    // Restored session → we're heading straight into a scenario/debrief
    // view; make sure the scenarios + debrief bundles are loaded before
    // the view mounts.
    await i18n.ensureExtras();
    renderFooter(i18n, {
      onExit: () => void showLanding(),
      onPrivacy: () => {
        setPageSeo('privacy');
        mount(root, PrivacyView({ i18n, onBack: () => void showLanding() }));
      },
      onDelete: async () => { await api.deleteSession().catch(() => undefined); clearState(); void showLanding(); },
    });
    if (snapshot.atEnd) {
      await showDebrief();
    } else if (snapshot.needsOnboarding) {
      showOnboarding();
    } else {
      await showScenario(snapshot.progress);
    }
    return;
  }

  await showLanding();
}

run().catch((err) => {
  root.appendChild(el('div', { class: 'error-banner' }, String((err as Error).message ?? err)));
});
